'use client';
import { SlideFrame } from '../SlideFrame';
import { useResource } from '@/lib/use-resource';

// Live MSFT-example slide — visual mirror of the 4 signal-cell popovers
// (Cong / Insid / Call / Earn) that appear on every row of the holdings
// table. Fetches `/api/stock-story/MSFT` + `/api/insider-trading` on render
// so the boxes always show today's data; no hardcoded snapshot to rot.
//
// Card 1: Congressional buying / selling (last 90 days)
// Card 2: Insider Form 4s (last 6 months)
// Card 3: Latest earnings call (tone + headline + one-liner)
// Card 4: Earnings momentum (verdict pill + last surprise + Y/Y)

interface CongTrade {
  transactionDate: string;
  legislator: string;
  chamber: 'Senate' | 'House';
  district: string;
  owner: string;
  type: string;
  amount: string;
}
interface StoryResp {
  symbol: string;
  congressionalActivity?: { totalLast90Days: number; buyCount: number; sellCount: number; trades: CongTrade[] } | null;
  latestTranscript?: { period: string; callDate: string; headline: string; tone: 'positive'|'neutral'|'cautious'|'negative'; oneLiner: string } | null;
  earningsMomentum?: { epsRevision: number | null; earningsSurprisePct: number | null } | null;
  lastEarnings?: { date?: string; period?: string; epsActual?: number; epsEstimate?: number; surprisePct?: number; yoyGrowthPct?: number | null } | null;
}
interface InsiderRow {
  buyCount?: number; sellCount?: number; buyValue?: number; sellValue?: number;
  netBuyRatio?: number; lastBuyDate?: string | null; lastSellDate?: string | null;
  form4Count?: number;
}
interface InsiderFile { stocks?: Record<string, InsiderRow> }

const SYM = 'MSFT';

function verdictLabel(surprisePct: number | null | undefined): { label: string; cls: string } {
  if (surprisePct == null || !Number.isFinite(surprisePct)) return { label: 'NO DATA', cls: 'bg-[#0f141c] border-[#1e293b] text-[#64748b]' };
  if (surprisePct > 10)   return { label: 'BIG BEAT',     cls: 'bg-emerald-500/15 border-emerald-400/50 text-emerald-300' };
  if (surprisePct > 2)    return { label: 'MODEST BEAT',  cls: 'bg-emerald-500/10 border-emerald-400/30 text-emerald-300/90' };
  if (surprisePct >= -2)  return { label: 'INLINE',       cls: 'bg-[#1e293b] border-[#334155] text-[#cbd5e1]' };
  if (surprisePct >= -10) return { label: 'MODEST MISS',  cls: 'bg-red-500/10 border-red-400/30 text-red-300/90' };
  return                  { label: 'BIG MISS', cls: 'bg-red-500/15 border-red-400/50 text-red-300' };
}

function toneClass(t: string): string {
  if (t === 'positive') return 'text-emerald-300';
  if (t === 'negative') return 'text-red-300';
  if (t === 'cautious') return 'text-amber-300';
  return 'text-[#cbd5e1]';
}

export function Slide04bMicrosoftExample() {
  const { data: story } = useResource<StoryResp>(`/api/stock-story/${SYM}`, 300_000);
  const { data: insiderFile } = useResource<InsiderFile>('/api/insider-trading', 300_000);
  const insider = insiderFile?.stocks?.[SYM] ?? null;

  const cong = story?.congressionalActivity ?? null;
  const transcript = story?.latestTranscript ?? null;
  const le = story?.lastEarnings ?? null;
  const verdict = verdictLabel(le?.surprisePct);

  return (
    <SlideFrame pageNum={14} title="THE VISUALIZER  —  PORTFOLIO TRACKING"
      banner="A worked example. Every name in the book gets this same four-feed dashboard, every day.">
      <div className="h-full flex flex-col gap-2 px-8 py-2">
        {/* MICROSOFT subject label — anchors the slide as the worked example
            for one specific name. Sits above the top-left Congressional card. */}
        <div className="text-[#c9a84c] text-[22px] font-bold tracking-wide leading-none">
          Microsoft <span className="text-[#94a3b8] text-[14px] font-normal">(MSFT)</span>
        </div>
        <div className="grid grid-cols-2 grid-rows-2 gap-3 flex-1 min-h-0">

        {/* CONGRESSIONAL */}
        <div className="bg-[#1e293b] rounded-2xl border border-[#c9a84c]/30 px-4 py-3 flex flex-col overflow-hidden">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[#c9a84c] text-[18px] font-bold tracking-wide">Congressional buying / selling</div>
            <div className="text-[10px] text-[#64748b] uppercase tracking-wider">last 90 days</div>
          </div>
          {cong && cong.totalLast90Days > 0 ? (
            <>
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-emerald-300 text-[28px] font-bold tabular-nums leading-none">{cong.buyCount}</span>
                <span className="text-[12px] text-emerald-300/80 uppercase tracking-wider">buy</span>
                <span className="text-[#64748b] mx-1">·</span>
                <span className="text-red-300 text-[28px] font-bold tabular-nums leading-none">{cong.sellCount}</span>
                <span className="text-[12px] text-red-300/80 uppercase tracking-wider">sell</span>
                <span className="ml-auto text-[11px] text-[#94a3b8]">{cong.totalLast90Days} disclosures</span>
              </div>
              <ul className="space-y-1 text-[12px] flex-1 overflow-hidden">
                {cong.trades.slice(0, 4).map((t, i) => (
                  <li key={i} className="grid grid-cols-[68px_1fr_auto] gap-2 items-baseline border-b border-[#1e293b] pb-1">
                    <span className="text-[10px] text-[#64748b] font-mono tabular-nums">{t.transactionDate}</span>
                    <span className="text-white truncate">
                      {t.legislator}
                      <span className="text-[#64748b] text-[10px] ml-1">({t.chamber}-{t.district}{t.owner && t.owner !== 'Self' ? ` · ${t.owner}` : ''})</span>
                    </span>
                    <span className="flex items-baseline gap-2">
                      <span className={`text-[10px] font-mono uppercase ${/purchase/i.test(t.type) ? 'text-emerald-300' : /sale|sold/i.test(t.type) ? 'text-red-300' : 'text-[#94a3b8]'}`}>{t.type}</span>
                      <span className="text-[10px] text-[#94a3b8] font-mono">{t.amount}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <div className="text-[12px] text-[#64748b] italic">No disclosures in window.</div>
          )}
        </div>

        {/* INSIDER */}
        <div className="bg-[#1e293b] rounded-2xl border border-[#c9a84c]/30 px-4 py-3 flex flex-col overflow-hidden">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[#c9a84c] text-[18px] font-bold tracking-wide">Insider Form 4s</div>
            <div className="text-[10px] text-[#64748b] uppercase tracking-wider">last 6 months</div>
          </div>
          {insider ? (
            <>
              <div className="grid grid-cols-2 gap-4 mb-3">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-emerald-300/70">Buys</div>
                  <div className="text-emerald-300 text-[22px] font-bold tabular-nums leading-none">{insider.buyCount ?? 0}</div>
                  {insider.buyValue != null && insider.buyValue > 0 && (
                    <div className="text-[11px] text-[#94a3b8] mt-0.5">${(insider.buyValue / 1e6).toFixed(1)}M</div>
                  )}
                  {insider.lastBuyDate && <div className="text-[9px] text-[#64748b]">last: {insider.lastBuyDate}</div>}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-red-300/70">Sells</div>
                  <div className="text-red-300 text-[22px] font-bold tabular-nums leading-none">{insider.sellCount ?? 0}</div>
                  {insider.sellValue != null && insider.sellValue > 0 && (
                    <div className="text-[11px] text-[#94a3b8] mt-0.5">${(insider.sellValue / 1e6).toFixed(1)}M</div>
                  )}
                  {insider.lastSellDate && <div className="text-[9px] text-[#64748b]">last: {insider.lastSellDate}</div>}
                </div>
              </div>
              <div className="text-[12px] border-t border-[#0f141c] pt-2">
                Net-buy ratio:{' '}
                <span className={`font-mono font-bold ${
                  insider.netBuyRatio == null ? 'text-[#64748b]' :
                  insider.netBuyRatio > 0.15 ? 'text-emerald-300' :
                  insider.netBuyRatio < -0.15 ? 'text-red-300' :
                  'text-amber-300/80'
                }`}>{insider.netBuyRatio == null ? '—' : insider.netBuyRatio.toFixed(2)}</span>
                <span className="text-[10px] text-[#64748b] ml-2">across {insider.form4Count ?? 0} filings</span>
              </div>
              <div className="text-[10px] text-[#64748b] italic mt-2 leading-snug">
                Range −1 (all selling) to +1 (all buying). Market-cap normalized.
              </div>
            </>
          ) : (
            <div className="text-[12px] text-[#64748b] italic">No Form 4 filings on file.</div>
          )}
        </div>

        {/* EARNINGS CALL */}
        <div className="bg-[#1e293b] rounded-2xl border border-[#c9a84c]/30 px-4 py-3 flex flex-col overflow-hidden">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[#c9a84c] text-[18px] font-bold tracking-wide">Latest earnings call</div>
            <div className="text-[10px] text-[#64748b] uppercase tracking-wider">AI-summarized</div>
          </div>
          {transcript ? (
            <>
              <div className="text-[10px] text-[#64748b] font-mono mb-2">
                {transcript.callDate} · {transcript.period} · <span className={`uppercase font-semibold ${toneClass(transcript.tone)}`}>{transcript.tone}</span>
              </div>
              <div className="text-[13px] font-semibold text-white leading-snug mb-2">{transcript.headline}</div>
              <div className="text-[12px] text-[#cbd5e1] italic leading-snug">{transcript.oneLiner}</div>
            </>
          ) : (
            <div className="text-[12px] text-[#64748b] italic">No transcript summary yet.</div>
          )}
        </div>

        {/* EARNINGS MOMENTUM */}
        <div className="bg-[#1e293b] rounded-2xl border border-[#c9a84c]/30 px-4 py-3 flex flex-col overflow-hidden">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[#c9a84c] text-[18px] font-bold tracking-wide">Earnings momentum</div>
            <div className="text-[10px] text-[#64748b] uppercase tracking-wider">most recent quarter</div>
          </div>
          {le ? (
            <>
              <div className="flex items-center gap-3 mb-3">
                <div className={`inline-flex items-center px-3 py-1 rounded-md border text-[13px] font-bold tracking-wide ${verdict.cls}`}>
                  {verdict.label}
                </div>
                <div className="text-[11px] text-[#94a3b8] font-mono">{le.period ?? ''}{le.date ? ` · ${le.date}` : ''}</div>
              </div>
              <div className="text-[12px] text-[#cbd5e1] mb-2">
                EPS <span className="font-mono text-white">${le.epsActual?.toFixed(2) ?? '—'}</span>
                {' vs '}<span className="font-mono">${le.epsEstimate?.toFixed(2) ?? '—'}</span> est
                {le.surprisePct != null && (
                  <>
                    {' · '}
                    <span className={`font-mono font-semibold ${le.surprisePct >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                      {le.surprisePct >= 0 ? '+' : ''}{le.surprisePct.toFixed(1)}%
                    </span>
                  </>
                )}
              </div>
              {le.yoyGrowthPct != null && (
                <div className="text-[12px] text-[#94a3b8]">
                  Y/Y growth: <span className={`font-mono ${le.yoyGrowthPct >= 0 ? 'text-emerald-300/80' : 'text-red-300/80'}`}>
                    {le.yoyGrowthPct >= 0 ? '+' : ''}{le.yoyGrowthPct.toFixed(1)}%
                  </span>
                </div>
              )}
              {story?.earningsMomentum?.epsRevision != null && (
                <div className="text-[11px] text-[#64748b] mt-2 pt-2 border-t border-[#0f141c]">
                  Analyst direction (z-score):{' '}
                  <span className={`font-mono ${
                    story.earningsMomentum.epsRevision > 0.3 ? 'text-emerald-300' :
                    story.earningsMomentum.epsRevision < -0.3 ? 'text-red-300' :
                    'text-[#cbd5e1]'
                  }`}>
                    {story.earningsMomentum.epsRevision >= 0 ? '+' : ''}{story.earningsMomentum.epsRevision.toFixed(2)}σ
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="text-[12px] text-[#64748b] italic">No recent earnings.</div>
          )}
        </div>

        </div>
      </div>
    </SlideFrame>
  );
}
